import { useCallback, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { translatePath } from "@/shared/i18n"
import { topUpTonCryptoBot } from "@/shared/api/endpoints"
import { paymentHistoryKeys } from "@/entities/history"
import { telegram } from "@/shared/lib/telegram"
import { makeErrorToast } from "@/shared/lib/toast-error"
import {
  AnalyticsEvent,
  analyticsErrorMessage,
  classifyDepositFailure,
  getAnalyticsDepositUiSource,
  registerPendingTonDeposit,
  trackAnalyticsEvent,
} from "@/shared/lib/analytics"

export type UseCryptoBotDepositOptions = {
  /** e.g. close DepositSheet */
  onSuccess?: () => void
}

/**
 * CryptoBot deposit: API creates invoice → open invoice URL in Telegram / browser.
 * No TonConnect wallet required.
 */
export function useCryptoBotDeposit(options: UseCryptoBotDepositOptions = {}) {
  const optsRef = useRef(options)
  optsRef.current = options

  const [isProcessing, setIsProcessing] = useState(false)
  const queryClient = useQueryClient()

  const depositViaCryptoBot = useCallback(
    async (amount: number) => {
      const { onSuccess } = optsRef.current
      setIsProcessing(true)
      try {
        const amountNanotons = Math.floor(amount * 1e9)
        const response = await topUpTonCryptoBot({ amount: amountNanotons })
        const invoiceUrl = response.payment_instructions.payload
        telegram.openTelegramLink(invoiceUrl)
        registerPendingTonDeposit("cryptobot", amount)
        trackAnalyticsEvent(AnalyticsEvent.DepositInitiated, {
          deposit_method: "cryptobot",
          amount_ton: amount,
          deposit_ui_source: getAnalyticsDepositUiSource(),
        })

        queryClient.invalidateQueries({
          queryKey: paymentHistoryKeys.infinite(),
        })
        onSuccess?.()
      } catch (error) {
        trackAnalyticsEvent(AnalyticsEvent.DepositFailed, {
          deposit_method: "cryptobot",
          amount_ton: amount,
          failure_reason: classifyDepositFailure(error),
          error_message: analyticsErrorMessage(error),
          deposit_ui_source: getAnalyticsDepositUiSource(),
        })
        const fallback = translatePath("toast.failedCryptoBotInvoice")
        if (error instanceof Error) {
          makeErrorToast({
            label: error.message || fallback,
          })
        } else {
          makeErrorToast({ label: fallback })
        }
      } finally {
        setIsProcessing(false)
      }
    },
    [queryClient]
  )

  return { depositViaCryptoBot, isProcessing }
}
